import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  ListItem,
  AccordionSummary,
  ListItemText,
  Box,
  Typography,
  Button,
  AccordionActions,
  TablePagination,
  TablePaginationProps,
} from "@material-ui/core";
import ExpandMore from "@material-ui/icons/ExpandMore";
import ConfirmDelete from "components/ConfirmDelete";
import {
  ListItemAccordion,
  StyledAccordionDetails,
} from "components/List/StyledComponents";
import { useDependencyModal } from "components/DependencyModal";
import { getFormattedTimeStamp } from "utils/dateUtils";
import apiRequest from "utils/apiRequestWithErrorCode";
import { DatasetAndSync } from "@dashboard-v3/api";
import { KeyValueContainer, KeyValueItem } from "../Styled";
import SyncStatus from "./SyncStatus";
import LastRunError from "./LastRunError";

interface ItemProps {
  content: DatasetAndSync;
  setLoading: Dispatch<SetStateAction<"ALL" | "MORE" | "ITEM">>;
  setLoadingItem: Dispatch<SetStateAction<string>>;
  fetchDatasets(): Promise<void>;
}

export default function DataSetItem({
  content,
  fetchDatasets,
  setLoading,
  setLoadingItem,
}: ItemProps) {
  const { id, name, updatedAt } = content;
  const { t } = useTranslation("datasets");
  const { enqueueSnackbar } = useSnackbar();
  const { errorHandler } = useDependencyModal();
  const dataRows = Object.keys(content.rows);
  const [pagination, setPagination] = useState(initPagination(dataRows.length));

  const getVisibleRows = () => {
    const { page, rowsPerPage } = pagination;
    const leftBoundary = page * rowsPerPage;
    const rightBoundary = page * rowsPerPage + rowsPerPage;

    return dataRows.slice(leftBoundary, rightBoundary);
  };

  const deleteDataSet = async (id: string) => {
    setLoading("ITEM");
    setLoadingItem(id);
    try {
      await apiRequest("DELETE", `/datasets/${id}`);
      await fetchDatasets();

      enqueueSnackbar(t("deleteSuccess", { name: content.name }), {
        variant: "success",
      });
    } catch (e) {
      errorHandler(e, t(`errors.deleteError`));
    } finally {
      setLoading(null);
      setLoadingItem(null);
    }
  };

  const onChangeRowsPerPage: TablePaginationProps["onRowsPerPageChange"] =
    e => {
      const { value } = e.target;
      const rowsPerPage = parseInt(value, 10);
      setPagination({ rowsPerPage, page: 0, total: pagination.total });
    };

  const onPageChange: TablePaginationProps["onPageChange"] = (_, page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  return (
    <ListItem disableGutters>
      <ListItemAccordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <ListItemText
            primary={
              <>
                <Box marginY="5px">
                  <Typography display="inline" style={{ fontWeight: "500" }}>
                    {name}
                  </Typography>
                </Box>
                <Box marginBottom="5px">
                  <Typography color="textSecondary" variant="body2">
                    {t("updatedAt", {
                      date: getFormattedTimeStamp(updatedAt, true),
                    })}
                  </Typography>
                </Box>
              </>
            }
          />
          <Box
            display="flex"
            marginRight="10px"
            justifyContent="space-between"
            alignItems="center"
            gridGap="10px"
          >
            <Box marginRight="1rem">
              <SyncStatus
                hadSync={content.hadSync}
                syncEnabled={content.syncEnabled}
                lastRun={content.lastRun}
              />
            </Box>

            <Button
              href={`/datasets/${content.id}/edit`}
              data-testid="datasets_edit-item-btn"
              variant="outlined"
              size="small"
              color="default"
              onClick={event => event.stopPropagation()}
            >
              {t("common:edit")}
            </Button>

            <ConfirmDelete
              message={t("listItem.confirmDeletion", { name })}
              size="small"
              variant="outlined"
              onAccept={() => deleteDataSet(id)}
            />
          </Box>
        </AccordionSummary>
        <StyledAccordionDetails>
          <KeyValueContainer>
            <LastRunError lastRun={content.lastRun} />

            {dataRows.length === 0 && (
              <Box textAlign="center" pb={2}>
                {t("listItem.empty")}
              </Box>
            )}

            {getVisibleRows().map(key => (
              <KeyValueItem key={key}>
                <div>
                  <span>{t("listItem.key")}</span>
                  {key}
                </div>
                <div>
                  <span>{t("listItem.value")}</span>
                  {content.rows[key]}
                </div>
              </KeyValueItem>
            ))}
          </KeyValueContainer>
        </StyledAccordionDetails>
        <AccordionActions>
          <TablePagination
            style={{ borderBottom: "none" }}
            page={pagination.page}
            rowsPerPage={pagination.rowsPerPage}
            count={pagination.total}
            rowsPerPageOptions={[5, 10, 20]}
            labelRowsPerPage={t("listItem.pagination.rowsPerPage")}
            onRowsPerPageChange={onChangeRowsPerPage}
            onPageChange={onPageChange}
          />
        </AccordionActions>
      </ListItemAccordion>
    </ListItem>
  );
}

function initPagination(total: number) {
  return () => ({
    total,
    page: 0,
    rowsPerPage: 5,
  });
}
